import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, BaseButton, CardV2, MenuList } from "pepsico-ds";
import { useSelector } from "react-redux";
import { appConstants } from "../../constants/appconstants";
import { getAuthHeader } from "../../utils/auth/unified-auth";
import { SpendAnalyticsSessionContext } from "./SpendAnalyticsSessionContext";
import SpendAnalyticsBadge from "./SpendAnalyticsBadge";
import "./MarketIntelligenceChat.scss";
import "./SpendAnalyticsAgent.scss";

const DEFAULT_SUGGESTED_PROMPTS = [
  "What is the OCC 11 forecast?",
  "Show top spend changes across suppliers this quarter.",
  "Which categories have the highest variance vs budget?",
];
const PROMPTS_PER_VIEW = 3;

const getSuggestedPromptsFromEnv = () => {
  const runtimePrompts =
    typeof window !== "undefined" ? window.SUGGESTED_PROMPTS : null;

  if (!Array.isArray(runtimePrompts)) {
    return DEFAULT_SUGGESTED_PROMPTS;
  }

  const parsedPrompts = runtimePrompts
    .map((prompt) =>
      typeof prompt === "string" ? prompt.trim() : String(prompt ?? "").trim(),
    )
    .filter(Boolean);

  return parsedPrompts.length > 0
    ? parsedPrompts
    : DEFAULT_SUGGESTED_PROMPTS;
};

const normalizeSessionHistory = (payload) => {
  if (!Array.isArray(payload)) return [];
  return [...payload]
    .filter((item) => item && typeof item === "object" && item.session_id)
    .map((item) => ({
      sessionId: item.session_id,
      conversationId: item.conversation_id || "",
      title: item.title || "Untitled session",
      lastMessage: item.last_message || "",
      messageCount: Number(item.message_count) || 0,
      isPinned: Boolean(item.is_pinned),
      createdAt: item.created_at || null,
      lastActiveAt: item.last_active_at || null,
    }))
    .sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      const aTime = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
      const bTime = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
      return bTime - aTime;
    });
};

const mapSessionSummaryItem = (item) => {
  if (!item || typeof item !== "object" || !item.session_id) return null;
  return {
    sessionId: item.session_id,
    conversationId: item.conversation_id || "",
    title: item.title || "Untitled session",
    lastMessage: item.last_message || "",
    messageCount: Number(item.message_count) || 0,
    isPinned: Boolean(item.is_pinned),
    createdAt: item.created_at || null,
    lastActiveAt: item.last_active_at || null,
    pinnedAt: item.pinned_at || null,
    updatedAt: item.updated_at || null,
  };
};

const getRelativeTime = (isoTime) => {
  if (!isoTime) return "-";
  const targetMs = new Date(isoTime).getTime();
  if (Number.isNaN(targetMs)) return "-";
  const diffSec = Math.max(0, Math.floor((Date.now() - targetMs) / 1000));
  if (diffSec < 60) return "just now";
  const units = [
    [31536000, "year"],
    [2592000, "month"],
    [86400, "day"],
    [3600, "hour"],
    [60, "minute"],
  ];
  for (const [seconds, label] of units) {
    const count = Math.floor(diffSec / seconds);
    if (count >= 1) {
      return `${count} ${label}${count > 1 ? "s" : ""} ago`;
    }
  }
  return "just now";
};

const SpendAnalyticsAgent = () => {
  const navigate = useNavigate();
  const sessionContext = useContext(SpendAnalyticsSessionContext);
  const userAccessData = useSelector((state) => state.getUserAccessReducer);
  const isAIUser = userAccessData?.isAiUser;
  const suggestedPrompts = React.useMemo(() => getSuggestedPromptsFromEnv(), []);

  const [message, setMessage] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [recentMenuOpenId, setRecentMenuOpenId] = useState(null);
  const [pinLoadingById, setPinLoadingById] = useState({});
  const [deleteTargetSessionId, setDeleteTargetSessionId] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [promptStartIndex, setPromptStartIndex] = useState(0);
  const [showAgentMenu, setShowAgentMenu] = useState(false);

  const maxPromptStartIndex = Math.max(
    0,
    suggestedPrompts.length - PROMPTS_PER_VIEW,
  );
  const visibleSuggestedPrompts = suggestedPrompts.slice(
    promptStartIndex,
    promptStartIndex + PROMPTS_PER_VIEW,
  );

  // Use context sessionHistory if available, fall back to local state
  const displaySessionHistory =
    sessionContext?.sessionHistory?.length > 0
      ? sessionContext.sessionHistory
      : sessionHistory;

  const fetchSessionHistory = async () => {
    setIsHistoryLoading(true);
    setHistoryError("");
    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/sessions/history`,
        {
          method: "GET",
          headers: { Authorization },
        },
      );
      if (!res.ok) {
        throw new Error(`Session history failed: ${res.status}`);
      }
      const data = await res.json();
      const normalized = normalizeSessionHistory(data);
      setSessionHistory(normalized);
      // Also update context so both pages stay in sync
      if (sessionContext?.setSessionHistory) {
        sessionContext.setSessionHistory(normalized);
      }
    } catch (err) {
      console.error("Session history error:", err);
      setSessionHistory([]);
      setHistoryError("Unable to load sessions.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  React.useEffect(() => {
    if (showSidebar) {
      fetchSessionHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar]);

  React.useEffect(() => {
    setPromptStartIndex((prev) => Math.min(prev, maxPromptStartIndex));
  }, [maxPromptStartIndex]);

  const handlePreviousPrompt = () => {
    setPromptStartIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextPrompt = () => {
    setPromptStartIndex((prev) => Math.min(maxPromptStartIndex, prev + 1));
  };

  const closeRecentMenu = () => {
    setRecentMenuOpenId(null);
  };

  const handleTogglePinSession = async (sessionEntry) => {
    if (!sessionEntry?.sessionId) return;
    const targetSessionId = sessionEntry.sessionId;
    const shouldPin = !Boolean(sessionEntry.isPinned);

    setPinLoadingById((prev) => ({ ...prev, [targetSessionId]: true }));
    setHistoryError("");

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/sessions/${targetSessionId}/pin`,
        {
          method: "PATCH",
          headers: {
            Authorization,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_pinned: shouldPin }),
        },
      );
      if (!res.ok) {
        throw new Error(`Pin session failed: ${res.status}`);
      }

      let payload = null;
      try {
        payload = await res.json();
      } catch (_err) {
        payload = null;
      }

      const normalizedItem = mapSessionSummaryItem(payload);
      const updateFn = (prev) => {
        const next = prev.map((entry) => {
          if (entry.sessionId !== targetSessionId) return entry;
          if (normalizedItem) return { ...entry, ...normalizedItem };
          const updatedAt = new Date().toISOString();
          return {
            ...entry,
            isPinned: shouldPin,
            pinnedAt: shouldPin ? updatedAt : null,
            updatedAt,
          };
        });
        return next.sort((a, b) => {
          if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
          const aTime = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
          const bTime = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
          return bTime - aTime;
        });
      };
      setSessionHistory(updateFn);
      // Also update context
      if (sessionContext?.sessionHistory) {
        const updated = updateFn([...sessionContext.sessionHistory]);
        if (sessionContext?.setSessionHistory) {
          sessionContext.setSessionHistory(updated);
        }
      }
    } catch (err) {
      console.error("Unable to update pin status:", err);
      setHistoryError("Unable to update pin status.");
    } finally {
      setPinLoadingById((prev) => ({ ...prev, [targetSessionId]: false }));
      closeRecentMenu();
    }
  };

  const handleConfirmDeleteSession = async () => {
    if (!deleteTargetSessionId) return;
    setIsDeletingSession(true);
    setHistoryError("");

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.SA_AI_BASE_URL}/api/v1/sessions/${deleteTargetSessionId}`,
        {
          method: "DELETE",
          headers: { Authorization },
        },
      );
      if (!res.ok) {
        throw new Error(`Delete session failed: ${res.status}`);
      }

      const updateFn = (prev) =>
        prev.filter((entry) => entry.sessionId !== deleteTargetSessionId);

      setSessionHistory(updateFn);
      // Also update context
      if (sessionContext?.sessionHistory) {
        const updated = updateFn([...sessionContext.sessionHistory]);
        if (sessionContext?.setSessionHistory) {
          sessionContext.setSessionHistory(updated);
        }
      }

      setDeleteTargetSessionId(null);
      closeRecentMenu();
    } catch (err) {
      console.error("Unable to delete session:", err);
      setHistoryError("Unable to delete session.");
    } finally {
      setIsDeletingSession(false);
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      navigate("/spend-analytics-ai-agent-conversations", {
        state: { message: message.trim() },
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="mi-chat-container sa-chat-container">
      <div className="mi-subheader">
        <button className="mi-back-btn" onClick={() => window.history.back()}>
          <Icon icon="arrow_back" />
        </button>
        <button
          className="sa-history-toggle-btn"
          onClick={() => setShowSidebar((prev) => !prev)}
          aria-label={
            showSidebar ? "Collapse chats panel" : "Expand chats panel"
          }
          title={showSidebar ? "Collapse chats panel" : "Expand chats panel"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1.66675 3.33398V16.6673H18.3334V3.33398H1.66675ZM16.6667 7.22565H14.5834V5.00065H16.6667V7.22565ZM14.5834 8.89232H16.6667V11.1173H14.5834V8.89232ZM3.33341 5.00065H12.9167V15.0007H3.33341V5.00065ZM14.5834 15.0007V12.7757H16.6667V15.0007H14.5834Z"
              fill="#004895"
            />
          </svg>
        </button>
        <SpendAnalyticsBadge variant="header" />
        <span
          className="mi-agent-name"
          style={{ cursor: "pointer" }}
          role="button"
          tabIndex={0}
          onClick={() => setShowAgentMenu((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setShowAgentMenu((prev) => !prev);
            }
          }}
        >
          Spend Analytics
        </span>
        <Icon
          icon="expand_more"
          style={{ cursor: "pointer" }}
          onClick={() => setShowAgentMenu((prev) => !prev)}
        />
      </div>

      {showAgentMenu && (
        <MenuList
          className="ph-agent-select"
          hideMenuListOnSelection
          customNoResultMessage=""
          emptyStateMessage=""
          height={240}
          isClearable
          onHideMenuList={() => setShowAgentMenu(false)}
          items={[
            {
              id: "/ai-assistant/knowledge-management",
              label: "Knowledge Management",
            },
            ...(isAIUser
              ? [
                  {
                    id: "/ai-assistant/market-intelligence",
                    label: "Market Intelligence",
                  },
                ]
              : []),
          ]}
          selectedItemCallback={function S8e(item) {
            if (item?.[0]?.id) {
              navigate(item[0].id);
            }
          }}
          selectedItems={[]}
          showSearch={false}
          width=""
          type="single"
          size="medium"
          style={{}}
          listHeaderLabel=""
        />
      )}

      <div className="mi-chat-body">
        {showSidebar && (
          <div className="mi-chat-sidebar">
            <div className="mi-chats-header">
              <span>Chats</span>
              <button
                className="mi-close-sidebar"
                onClick={() => setShowSidebar(false)}
              >
                <Icon icon="close" />
              </button>
            </div>
            <button className="mi-new-chat-btn sa-new-chat-btn">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M15.8334 10.8327H10.8334V15.8327H9.16675V10.8327H4.16675V9.16602H9.16675V4.16602H10.8334V9.16602H15.8334V10.8327Z"
                  fill="#004895"
                />
              </svg>
              <span>New chat</span>
            </button>
            <div className="mi-recents">
              <div className="mi-recents-label">Recents</div>
              {isHistoryLoading && (
                <div className="mi-recent-item">Loading sessions...</div>
              )}
              {!isHistoryLoading && historyError && (
                <div className="mi-recent-item">{historyError}</div>
              )}
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.length === 0 && (
                  <div className="mi-recent-item">No sessions</div>
                )}
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.map((chat) => (
                  <div key={chat.sessionId} className="sa-conv-recent-row">
                    <button
                      className="mi-recent-item sa-conv-recent-item"
                      onClick={() =>
                        navigate("/spend-analytics-ai-agent-conversations", {
                          state: {
                            session_id: chat.sessionId,
                            conversation_id: chat.conversationId,
                          },
                        })
                      }
                      title={chat.title}
                    >
                      <div className="sa-conv-recent-title-wrap">
                        <span className="sa-conv-recent-title">
                          {chat.title}
                        </span>
                        <span className="sa-conv-recent-time">
                          {getRelativeTime(chat.lastActiveAt)}
                        </span>
                      </div>
                      {chat.isPinned && (
                        <span className="sa-conv-recent-pinned-badge">
                          Pinned
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="sa-conv-recent-actions-trigger"
                      aria-label="Session actions"
                      title="Session actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRecentMenuOpenId((prev) =>
                          prev === chat.sessionId ? null : chat.sessionId,
                        );
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M9 3.25C9.69036 3.25 10.25 2.69036 10.25 2C10.25 1.30964 9.69036 0.75 9 0.75C8.30964 0.75 7.75 1.30964 7.75 2C7.75 2.69036 8.30964 3.25 9 3.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 10.25C9.69036 10.25 10.25 9.69036 10.25 9C10.25 8.30964 9.69036 7.75 9 7.75C8.30964 7.75 7.75 8.30964 7.75 9C7.75 9.69036 8.30964 10.25 9 10.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 17.25C9.69036 17.25 10.25 16.6904 10.25 16C10.25 15.3096 9.69036 14.75 9 14.75C8.30964 14.75 7.75 15.3096 7.75 16C7.75 16.6904 8.30964 17.25 9 17.25Z"
                          fill="#4B4B4B"
                        />
                      </svg>
                    </button>
                    {recentMenuOpenId === chat.sessionId && (
                      <div className="sa-conv-recent-actions-menu" role="menu">
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item"
                          disabled={Boolean(pinLoadingById[chat.sessionId])}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePinSession(chat);
                          }}
                        >
                          {pinLoadingById[chat.sessionId]
                            ? "Updating..."
                            : chat.isPinned
                              ? "Unpin"
                              : "Pin"}
                        </button>
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item sa-conv-recent-actions-item--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTargetSessionId(chat.sessionId);
                            closeRecentMenu();
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="mi-chat-main">
          <div className="mi-chat-welcome">
            <SpendAnalyticsBadge variant="hero" className="mi-welcome-icon" />
            <h1 className="mi-welcome-title">
              Welcome to Spend Analytics. How can I help?
            </h1>
            <p className="mi-welcome-subtitle">
              Get instant answers to your spend questions through a simple
              conversational experience. Ask questions, uncover insights, and
              analyse spend across suppliers, categories, regions, and business
              units - without relying on dashboards, spreadsheets, or manual
              reports. All within a governed and trusted data environment.
            </p>
          </div>

          <div className="mi-chat-input-area">
            <div className="mi-input-wrapper">
              <input
                type="text"
                className="mi-input"
                placeholder="Send a message to Spend Analytics."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button
                className="mi-send-btn"
                onClick={handleSendMessage}
                disabled={!message.trim()}
              >
                <Icon icon="send" />
              </button>
            </div>
            <p className="mi-input-disclaimer">
              By messaging the AI agent, you confirm that you have read and
              agree to our{" "}
              <a
                href="https://pepsicogsearchaiprod.blob.core.windows.net/pepgpt-files/PepsiCo_Code_of_conduct_for_PepGPT.pdf?sp=r&st=2025-03-25T06:22:44Z&se=2028-03-25T14:22:44Z&spr=https&sv=2024-11-04&sr=b&sig=Npv1BuV9QUzSaDssiOASAeQMigaf%2F7HLnsUHu2f2CxE%3D"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms & Conditions
              </a>{" "}
              and{" "}
              <a
                href="https://www.mypepsico.com/mypepsico?id=mypepsico_kb_article_view&sysparm_article=KB0160983&table=kb_knowledge&searchTerm=PEPSICO%20EMPLOYEE%20PRIVACY"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Notice
              </a>
              . Please do not share sensitive information. Chats may be reviewed
              and used to improve our models.
            </p>
            <div className="mi-suggested-prompts">
              <BaseButton
                className="mi-prompt-nav"
                disabled={promptStartIndex === 0}
                onClick={handlePreviousPrompt}
                aria-label="Previous suggested prompts"
                size="large"
                isShowIcon
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M14.0051 15.0499L8.91617 9.94987L14.0051 4.84987L12.4384 3.2832L5.77173 9.94987L12.4384 16.6165L14.0051 15.0499Z"
                      fill="#B1B1B1"
                    />
                  </svg>
                }
              />
              <div className="mi-prompt-cards-wrapper">
                {visibleSuggestedPrompts.map((prompt, idx) => (
                  <CardV2
                    key={`${prompt}-${promptStartIndex + idx}`}
                    className="mi-prompt-card"
                    state="interactive"
                    type="basic"
                    cardBody={{
                      type: "slot",
                      slot: (
                        <button
                          type="button"
                          className="mi-prompt-card-btn"
                          onClick={() => setMessage(prompt)}
                        >
                          <span className="mi-prompt-card-copy">
                            <span className="mi-prompt-card-text">
                              {prompt}
                            </span>
                          </span>
                        </button>
                      ),
                    }}
                  />
                ))}
              </div>
              <BaseButton
                className="mi-prompt-nav"
                disabled={promptStartIndex >= maxPromptStartIndex}
                onClick={handleNextPrompt}
                aria-label="Next suggested prompts"
                size="large"
                isShowIcon
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                    style={{ transform: "rotate(180deg)" }}
                  >
                    <path
                      d="M14.0051 15.0499L8.91617 9.94987L14.0051 4.84987L12.4384 3.2832L5.77173 9.94987L12.4384 16.6165L14.0051 15.0499Z"
                      fill="#B1B1B1"
                    />
                  </svg>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {deleteTargetSessionId && (
        <div className="sa-conv-delete-modal-backdrop" role="presentation">
          <div
            className="sa-conv-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Delete chat"
          >
            <h3>Delete chat?</h3>
            <p>
              Are you sure you want to delete this chat? This action cannot be
              undone.
            </p>
            <div className="sa-conv-delete-modal-actions">
              <button
                type="button"
                onClick={() => setDeleteTargetSessionId(null)}
                disabled={isDeletingSession}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sa-conv-delete-confirm-btn"
                onClick={handleConfirmDeleteSession}
                disabled={isDeletingSession}
              >
                {isDeletingSession ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpendAnalyticsAgent;
